import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Plus, ImageIcon, Check, AlertCircle, Pencil, Search, ChevronRight, Sparkles, Loader2, X, Video } from 'lucide-react';

import { supabase } from '../lib/supabase';
import { GENERATE_AD_URL, GENERATE_AUTH_TOKEN } from '../config';
import VariantForm, { ProductImage, Variant } from '../components/VariantForm';
import GeneratedImagesInline from '../components/GeneratedImagesInline';
import LoadingAnimation from '../components/LoadingAnimation';
import SubscriptionLimitModal from '../components/SubscriptionLimitModal';
import { useLocation, useNavigate } from 'react-router-dom';
import heic2any from 'heic2any';
import { loadLastCrearAnuncioState, mergeLastCrearAnuncioState } from '../utils/crearAnuncioStorage';
import type { CrearAnuncioModo } from '../utils/crearAnuncioStorage';
import { getCachedImageByKey, releaseCachedByKey } from '../utils/imageCache';
import { trackInteraction } from '../utils/trackInteraction';

const FASHION_LABEL = 'Moda e Indumentaria';
const AR_TZ = 'America/Argentina/Buenos_Aires';

const getGreeting = () => {
  try {
    const hourStr = new Intl.DateTimeFormat('es-AR', {
      hour: 'numeric',
      hour12: false,
      timeZone: AR_TZ,
    }).format(new Date());
    const hour = parseInt(hourStr, 10);
    if (hour >= 6 && hour < 15) return 'Buenos días';
    if (hour >= 15 && hour < 20) return 'Buenas tardes';
    return 'Buenas noches';
  } catch (_e) {
    const hour = new Date().getHours();
    if (hour >= 6 && hour < 15) return 'Buenos días';
    if (hour >= 15 && hour < 20) return 'Buenas tardes';
    return 'Buenas noches';
  }
};

export const SeleccionarProductosAnuncio: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const modo = useMemo<CrearAnuncioModo>(() => {
    const m = new URLSearchParams(location.search).get('modo');
    return m === 'personalizado' ? 'personalizado' : 'rapido';
  }, [location.search]);

  const savedStateRef = useRef(loadLastCrearAnuncioState());

  const [variants, setVariants] = useState<Variant[]>([]);
  const [selectedVariants, setSelectedVariants] = useState<string[]>(() => {
    const arr = savedStateRef.current?.selectedVariantIds ?? [];
    return Array.isArray(arr) ? arr.map((n: any) => String(n)) : [];
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [signedUrls, setSignedUrls] = useState<Record<string, { full: string; thumbnail: string }>>({});

  // Add product modal state
  const [showAddForm, setShowAddForm] = useState(false);
  const [savingProduct, setSavingProduct] = useState(false);
  const [imageError, setImageError] = useState<string | null>(null);
  const [newProduct, setNewProduct] = useState<{ nombre: string; images: ProductImage[] }>({ nombre: '', images: [] });

  // Edit product modal state (mirror SocialMedia behavior)
  const [editingVariant, setEditingVariant] = useState<Variant | null>(null);
  const [uploadingImages, setUploadingImages] = useState<Record<string, boolean>>({});

  // Search term
  const [query, setQuery] = useState('');

  // Company/cache + lazy-loading helpers
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [isFashion, setIsFashion] = useState<boolean | null>(() => {
    const last = loadLastCrearAnuncioState();
    if (!last?.rubro) return null;
    return last.rubro === FASHION_LABEL;
  });
  const [userRubro, setUserRubro] = useState<string>(() => {
    const last = loadLastCrearAnuncioState();
    return (last?.rubro as string) ?? FASHION_LABEL;
  });
  const [shortName, setShortName] = useState<string>('');
  const [companyName, setCompanyName] = useState<string>('');
  const [isFreePlan, setIsFreePlan] = useState<boolean>(false);
  const cardRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadingImageKeysRef = useRef<Set<string>>(new Set());
  const cachedKeysRef = useRef<Set<string>>(new Set());

  // Upsell banner state (free plan users who already generated at least one image)
  const [showUpsell, setShowUpsell] = useState(false);
  // Brand analysis banner state (paid users without media_agent_analysis)
  // Hide if coming from BrandDescription with hideBrandBanner state
  const [showBrandAnalysisBanner, setShowBrandAnalysisBanner] = useState(() => {
    const state = location.state as { hideBrandBanner?: boolean } | null;
    return state?.hideBrandBanner ? false : false;
  });

  // Repeat previous generation (session-based)
  const [hasGeneratedThisSession, setHasGeneratedThisSession] = useState<boolean>(() => {
    try { return window.sessionStorage.getItem('atlas:crear-anuncio:has-generated') === '1'; } catch { return false; }
  });
  const [showRepeatConfirm, setShowRepeatConfirm] = useState(false);
  const [generatingRepeat, setGeneratingRepeat] = useState(false);
  const [completeRepeatNow, setCompleteRepeatNow] = useState<boolean>(false);
  const [pendingRepeatNav, setPendingRepeatNav] = useState<{ url: string; navState: any } | null>(null);
  const pendingRepeatNavRef = useRef<{ url: string; navState: any } | null>(null);
  const [repeatError, setRepeatError] = useState<string | null>(null);
  const [repeatResultsOpen, setRepeatResultsOpen] = useState(false);
  const [repeatGeneratedIds, setRepeatGeneratedIds] = useState<number[]>([]);
  const [repeatCompanyId, setRepeatCompanyId] = useState<string>('');
  const navigatedAfterRepeatRef = useRef<boolean>(false);
  const progressDurationMs = useMemo(() => (modo === 'rapido' ? 70000 : 35000), [modo]);
  const [showSubscriptionLimitModal, setShowSubscriptionLimitModal] = useState<boolean>(false);
  const [subscriptionLimitType, setSubscriptionLimitType] = useState<'free-trial-ended' | 'plan-limit-reached'>('free-trial-ended');
  const [isBetaTester, setIsBetaTester] = useState<boolean>(false);
  const [showVideoPlanRestriction, setShowVideoPlanRestriction] = useState(false);
  const [showVideoMultipleProductsWarning, setShowVideoMultipleProductsWarning] = useState(false);
  const [userPlan, setUserPlan] = useState<string>('gratis');
  const [userCountry, setUserCountry] = useState<string>('Argentina');
  const [openingBillingPortal, setOpeningBillingPortal] = useState(false);
  const [randomMessage, setRandomMessage] = useState<string | null>(null);

  // Scroll to top on mount (iOS Safari fix)
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  useEffect(() => {
    fetchVariants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch random message for greeting
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data: mensajes, error } = await supabase
          .from('mensajes_inicio')
          .select('mensaje');
        if (!error && mensajes && mensajes.length > 0 && mounted) {
          const idx = Math.floor(Math.random() * mensajes.length);
          setRandomMessage(mensajes[idx].mensaje);
        }
      } catch (e) {
        console.error('Error loading random message:', e);
      }
    })();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    return () => {
      cachedKeysRef.current.forEach((k) => releaseCachedByKey(k));
      cachedKeysRef.current.clear();
    };
  }, []);

  // Check if user is a beta tester
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) { if (mounted) setIsBetaTester(false); return; }
        const { data: profile } = await supabase
          .from('profiles')
          .select('id_company')
          .eq('id', user.id)
          .single();
        const idCompany = (profile as any)?.id_company as string | null;
        if (!idCompany) { if (mounted) setIsBetaTester(false); return; }
        const { data: company } = await supabase
          .from('company_profiles')
          .select('beta_tester')
          .eq('id_company', idCompany)
          .single();
        if (mounted) setIsBetaTester(((company as any)?.beta_tester === true));
      } catch {
        if (mounted) setIsBetaTester(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const fetchVariants = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');
      

      const { data: profile } = await supabase
        .from('profiles')
        .select('id_company, name_short')
        .eq('id', user.id)
        .single();
      

      let idCompany = (profile as any)?.id_company as string | null;
      // profiles now has id_company; no fallback to company_id needed
      
      if (!idCompany) throw new Error('No company associated with user');
      setCompanyId(idCompany);
      setShortName((profile as any).name_short || '');

      // Check plan and generated images to decide if we show upsell banner
      try {
        const { data: company } = await supabase
          .from('company_profiles')
          .select('plan, rubro, nombre, pais')
          .eq('id_company', idCompany)
          .single();
        
        setCompanyName((company as any)?.nombre || '');
        const plan = (company as any)?.plan || 'gratis';
        setUserPlan(plan);
        setUserCountry((company as any)?.pais || 'Argentina');
        const isPaidPlan = plan && plan !== 'gratis';
        setIsFreePlan(!isPaidPlan);
        
        if (!isPaidPlan) {
          const { count } = await supabase
            .from('imagenes_generadas')
            .select('id', { count: 'exact', head: true })
            .neq('has_error', true)
            .eq('id_company', idCompany);
          
          setShowUpsell((count || 0) >= 5);
          setShowBrandAnalysisBanner(false);
        } else {
          setShowUpsell(false);
          // Check if coming from BrandDescription with hideBrandBanner
          const navState = location.state as { hideBrandBanner?: boolean } | null;
          if (navState?.hideBrandBanner) {
            setShowBrandAnalysisBanner(false);
          } else {
            // Check if paid user has media_agent_analysis
            try {
              const { data: config } = await supabase
                .from('config')
                .select('media_agent_analysis')
                .eq('id_company', idCompany)
                .maybeSingle();
              const hasAnalysis = !!(config?.media_agent_analysis);
              setShowBrandAnalysisBanner(!hasAnalysis);
            } catch {
              setShowBrandAnalysisBanner(false);
            }
          }
        }
        const rubroValue = (company?.rubro as string | null) ?? FASHION_LABEL;
        setIsFashion(rubroValue === FASHION_LABEL);
        setUserRubro(rubroValue);
        mergeLastCrearAnuncioState({ rubro: rubroValue });
      } catch {
        setShowUpsell(false);
        setShowBrandAnalysisBanner(false);
      }

      // Fetch variants from public schema filtered by id_company
      const { data: variantsData, error: variantsError } = await supabase
        .from('variantes')
        .select('id, nombre, created_at')
        .eq('activo', true)
        .eq('id_company', idCompany)
        .order('created_at', { ascending: false });

      if (variantsError) throw variantsError;
      

      let processed: Variant[] = [];
      if (variantsData && variantsData.length > 0) {
        const variantIds = variantsData.map(v => v.id);
        
        const { data: imagesData, error: imagesError } = await supabase
          .from('productos_imagenes')
          .select('id, url, principal, orden, variante_id')
          .eq('id_company', idCompany)
          .in('variante_id', variantIds);
        if (imagesError) throw imagesError;
        

        const imagesByVariantId: Record<string, ProductImage[]> = {};
        imagesData?.forEach((image: any) => {
          const variantId = image.variante_id as string;
          const entry: ProductImage = {
            id: image.id,
            url: image.url,
            principal: image.principal,
            orden: image.orden,
            producto_variante_id: 0,
            variante_id: variantId,
          };
          if (!imagesByVariantId[variantId]) imagesByVariantId[variantId] = [];
          imagesByVariantId[variantId].push(entry);
        });

        processed = variantsData.map(v => ({ id: v.id as string, nombre: v.nombre, images: imagesByVariantId[v.id as string] || [] }));
      }

      setVariants(processed);
      setSelectedVariants((prev) => {
        if (!processed.length) {
          if (prev.length === 0) return prev;
          mergeLastCrearAnuncioState({ selectedVariantIds: [] });
          return [];
        }
        const available = new Set(processed.map((v) => v.id));
        const filtered = prev.filter((id) => available.has(id));
        if (filtered.length === prev.length) return prev;
        mergeLastCrearAnuncioState({ selectedVariantIds: filtered });
        return filtered;
      });
      // Lazy-load signed URLs as cards enter viewport
      setSignedUrls({});
    } catch (err) {
      console.error('Error fetching variants:', err);
      setError(err instanceof Error ? err.message : 'Error al cargar productos');
    } finally {
      setLoading(false);
    }
  };

  // Check if user can access video generation (Pro or Ultra plans only)
  const canAccessVideoGeneration = userPlan === 'negocio' || userPlan === 'enterprise';
  const isArgentina = (userCountry || '').toLowerCase() === 'argentina';

  const handleVideoGenerationClick = () => {
    // Check if more than 1 product is selected
    if (selectedVariants.length > 1) {
      setShowVideoMultipleProductsWarning(true);
      return;
    }
    if (canAccessVideoGeneration) {
      navigate(`/redes-sociales/crear-anuncio/disenar-video?ids=${selectedVariants.join(',')}`);
    } else {
      setShowVideoPlanRestriction(true);
    }
  };

  const handleOpenBillingPortal = async () => {
    try {
      setOpeningBillingPortal(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuario no autenticado');
      const resp = await fetch('https://ai-agency-dt7d.onrender.com/webhook/billing-portal', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': GENERATE_AUTH_TOKEN },
        body: JSON.stringify({ user_id: user.id })
      });
      if (!resp.ok) throw new Error('No se pudo abrir el portal de facturación');
      const data = await resp.json();
      if (data?.url) {
        window.location.href = data.url as string;
      } else {
        throw new Error('URL inválida recibida');
      }
    } catch (e: any) {
      alert(e?.message || 'Error al abrir el portal de facturación');
      setOpeningBillingPortal(false);
    }
  };

  const handleVariantSelect = useCallback((variantId: string) => {
    setSelectedVariants((prev) => {
      let next: string[];
      if (prev.includes(variantId)) {
        next = prev.filter((id) => id !== variantId);
      } else if (prev.length < 3) {
        next = [...prev, variantId];
      } else {
        next = [...prev.slice(1), variantId];
      }
      mergeLastCrearAnuncioState({ selectedVariantIds: next });
      return next;
    });
  }, []);

  // Edit icon: open VariantForm with the selected variant (like SocialMedia)
  const handleEditIconClick = (e: React.MouseEvent, variantId: string) => {
    e.stopPropagation();
    const variant = variants.find(v => v.id === variantId) || null;
    if (variant) setEditingVariant(variant);
  };

  const handleSaveProduct = async () => {
    const isEditing = !!editingVariant;
    const productName = isEditing ? (editingVariant?.nombre || '') : newProduct.nombre;
    if (!productName) return;

    try {
      setSavingProduct(true);
      setImageError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { data: profile } = await supabase
        .from('profiles')
        .select('id_company')
        .eq('id', user.id)
        .single();

      if (!(profile as any)?.id_company) throw new Error('No company associated with user');
      const idCompany = (profile as any).id_company as string;

      if (isEditing && editingVariant) {
        // Update variant name
        const { error: updError } = await supabase
          .from('variantes')
          .update({ nombre: productName })
          .eq('id', editingVariant.id)
          .eq('id_company', idCompany);
        if (updError) throw updError;

        // Close modal and refresh list to reflect changes
        setEditingVariant(null);
        await fetchVariants();
      } else {
        // 1) Create product
        const { data: producto, error: productoError } = await supabase
          .from('productos')
          .insert({ id_company: idCompany, nombre: newProduct.nombre, tiene_variantes: false })
          .select('id')
          .single();
        if (productoError) throw productoError;

        const productId = String(producto.id);

        // 2) Create variant for that product
        const { data: variant, error: variantError } = await supabase
          .from('variantes')
          .insert({ id_company: idCompany, nombre: newProduct.nombre, activo: true, product_id: productId })
          .select('id')
          .single();
        if (variantError) throw variantError;

        const variantId = variant.id as string;

        // 3) Upload images (full + opt) and insert DB rows storing only filename
        for (let i = 0; i < newProduct.images.length; i++) {
          const imgData = newProduct.images[i];
          if (!imgData.tempFile) continue;

          // Validate file size (limit: 100 MB)
          const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100 MB
          if (imgData.tempFile.size > MAX_FILE_SIZE) {
            throw new Error('El archivo supera el límite de 100 MB. Reducí el tamaño e intentá de nuevo.');
          }

          // Unique filename
          const timestamp = Date.now();
          const originalName = imgData.fileName || imgData.tempFile.name;
          const fileBaseName = `${timestamp}_${originalName}`;

          // Upload full
          const fullPath = `${idCompany}/${variantId}/full/${fileBaseName}`;
          const { error: fullErr } = await supabase.storage.from('productos').upload(fullPath, imgData.tempFile);
          if (fullErr) throw fullErr;

          // Create optimized thumbnail using canvas
          try {
            const img = new Image();
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            if (!ctx) throw new Error('Failed to get canvas context');

            const blob = await new Promise<Blob>((resolve, reject) => {
              img.onload = () => {
                const target = 300;
                let { width, height } = img;
                if (width > height) {
                  if (width > target) {
                    height = Math.round(height * (target / width));
                    width = target;
                  }
                } else if (height > target) {
                  width = Math.round(width * (target / height));
                  height = target;
                }
                canvas.width = width; canvas.height = height;
                ctx.drawImage(img, 0, 0, width, height);
                canvas.toBlob((b) => b ? resolve(b) : reject(new Error('Blob failed')), 'image/jpeg', 0.8);
              };
              img.onerror = () => reject(new Error('Image load error'));
              img.src = URL.createObjectURL(imgData.tempFile as File);
            });

            const optPath = `${idCompany}/${variantId}/opt/${fileBaseName}`;
            const { error: optErr } = await supabase.storage.from('productos').upload(optPath, blob);
            if (optErr) console.warn('Optimized upload failed, continuing with full only', optErr);
          } catch (optErr) {
            console.warn('Error creating optimized image, continuing', optErr);
          }

          // Insert DB row with filename only
          const { error: imageError } = await supabase
            .from('productos_imagenes')
            .insert({ id_company: idCompany, variante_id: variantId, url: fileBaseName, principal: imgData.principal || i === 0, orden: imgData.orden || i + 1 });
          if (imageError) throw imageError;
        }

        // Webhook: notify after variant is created and images uploaded
        try {
          await fetch('https://ai-agency-dt7d.onrender.com/webhook/categorizar_producto', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': GENERATE_AUTH_TOKEN },
            body: JSON.stringify({ user_id: user.id, product_id: productId })
          });
        } catch {}

        // Reset and refresh
        setShowAddForm(false);
        setNewProduct({ nombre: '', images: [] });
        await fetchVariants();
      }
    } catch (err) {
      console.error('Error saving product:', err);
      setImageError(err instanceof Error ? err.message : 'Error al guardar producto');
    } finally {
      setSavingProduct(false);
    }
  };

  // Handlers for editing flow (mirroring SocialMedia)
  const handleSetPrincipalImage = async (variantId: string, imageId: number) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { data: profile } = await supabase
        .from('profiles')
        .select('id_company')
        .eq('id', user.id)
        .single();

      if (!(profile as any)?.id_company) throw new Error('No company associated with user');
      const idCompany = (profile as any).id_company as string;

      await supabase
        .from('productos_imagenes')
        .update({ principal: false })
        .eq('variante_id', variantId)
        .eq('id_company', idCompany);

      await supabase
        .from('productos_imagenes')
        .update({ principal: true })
        .eq('id', imageId)
        .eq('id_company', idCompany);

      const { data: imagesData } = await supabase
        .from('productos_imagenes')
        .select('id, url, principal, orden, variante_id')
        .eq('variante_id', variantId)
        .eq('id_company', idCompany)
        .order('orden', { ascending: true });

      const updatedImages: ProductImage[] = (imagesData || []).map((img: any) => ({
        id: img.id,
        url: img.url,
        principal: img.principal,
        orden: img.orden,
        producto_variante_id: 0,
        variante_id: img.variante_id,
      }));

      setEditingVariant(prev => prev && prev.id === variantId ? { ...prev, images: updatedImages } : prev);
      setVariants(prev => prev.map(v => v.id === variantId ? { ...v, images: updatedImages } : v));
    } catch (err) {
      console.error('Error setting principal image:', err);
      setError(err instanceof Error ? err.message : 'Error al actualizar imagen principal');
    }
  };

  const handleImageUpload = async (variantId: string, file: File) => {
    try {
      setUploadingImages(prev => ({ ...prev, [String(variantId)]: true }));
      setImageError(null);

      // Validate file size (limit: 100 MB)
      const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100 MB
      if (file.size > MAX_FILE_SIZE) {
        setImageError('El archivo supera el límite de 100 MB. Reducí el tamaño e intentá de nuevo.');
        return;
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { data: profile } = await supabase
        .from('profiles')
        .select('id_company')
        .eq('id', user.id)
        .single();

      if (!(profile as any)?.id_company) throw new Error('No company associated with user');

      // Convert HEIC if needed
      let processedFile: File = file;
      if (
        file.type === 'image/heic' ||
        file.type === 'image/heif' ||
        file.name.toLowerCase().endsWith('.heic') ||
        file.name.toLowerCase().endsWith('.heif')
      ) {
        try {
          const convertedBlob = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.8 });
          processedFile = new File([convertedBlob as Blob], file.name.replace(/\.heic$/i, '.jpg').replace(/\.heif$/i, '.jpg'), { type: 'image/jpeg' });
        } catch (e) {
          throw new Error('No pudimos convertir la imagen HEIC. Probá con una imagen en formato png o jpg');
        }
      }

      const timestamp = Date.now();
      const originalName = processedFile.name;
      const fileBaseName = `${timestamp}_${originalName}`;

      // Upload full
      const idCompany = (profile as any).id_company as string;
      const fullPath = `${idCompany}/${variantId}/full/${fileBaseName}`;
      const { error: fullErr } = await supabase.storage.from('productos').upload(fullPath, processedFile);
      if (fullErr) throw fullErr;

      // Create and upload optimized
      try {
        const img = new Image();
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('Failed to get canvas context');

        const blob = await new Promise<Blob>((resolve, reject) => {
          img.onload = () => {
            const target = 300;
            let { width, height } = img as HTMLImageElement;
            if (width > height) {
              if (width > target) {
                height = Math.round(height * (target / width));
                width = target;
              }
            } else if (height > target) {
              width = Math.round(width * (target / height));
              height = target;
            }
            canvas.width = width; canvas.height = height;
            ctx.drawImage(img, 0, 0, width, height);
            canvas.toBlob((b) => b ? resolve(b) : reject(new Error('Blob failed')), 'image/jpeg', 0.8);
          };
          img.onerror = () => reject(new Error('Image load error'));
          img.src = URL.createObjectURL(processedFile);
        });

        const optPath = `${idCompany}/${variantId}/opt/${fileBaseName}`;
        const { error: optErr } = await supabase.storage.from('productos').upload(optPath, blob);
        if (optErr) console.warn('Optimized upload failed, continuing with full only', optErr);
      } catch (optErr) {
        console.warn('Error creating optimized image, continuing', optErr);
      }

      // Insert DB row
      const makePrincipal = editingVariant?.id === variantId && !(editingVariant.images || []).some(img => img.principal);
      const { error: imageError } = await supabase
        .from('productos_imagenes')
        .insert({ id_company: idCompany, variante_id: variantId, url: fileBaseName, principal: !!makePrincipal, orden: (editingVariant?.images?.length || 0) + 1 });
      if (imageError) throw imageError;

      // Refresh images
      const { data: imagesData } = await supabase
        .from('productos_imagenes')
        .select('id, url, principal, orden, variante_id')
        .eq('variante_id', variantId)
        .eq('id_company', idCompany)
        .order('orden', { ascending: true });

      const updatedImages: ProductImage[] = (imagesData || []).map((img: any) => ({
        id: img.id,
        url: img.url,
        principal: img.principal,
        orden: img.orden,
        producto_variante_id: 0,
        variante_id: img.variante_id,
      }));

      setEditingVariant(prev => prev && prev.id === variantId ? { ...prev, images: updatedImages } : prev);
      setVariants(prev => prev.map(v => v.id === variantId ? { ...v, images: updatedImages } : v));
    } catch (err) {
      console.error('Error uploading image:', err);
      setImageError(err instanceof Error ? err.message : 'Error al subir la imagen');
    } finally {
      setUploadingImages(prev => ({ ...prev, [String(variantId)]: false }));
    }
  };

  const handleDeleteImage = async (variantId: string, imageId: number) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { data: profile } = await supabase
        .from('profiles')
        .select('id_company')
        .eq('id', user.id)
        .single();

      if (!(profile as any)?.id_company) throw new Error('No company associated with user');
      const idCompany = (profile as any).id_company as string;

      // Get image record to know filename
      const { data: imageData } = await supabase
        .from('productos_imagenes')
        .select('id, url')
        .eq('id', imageId)
        .eq('id_company', idCompany)
        .single();

      const filename = imageData?.url || '';

      if (filename && !filename.startsWith('http')) {
        const fullPath = `${idCompany}/${variantId}/full/${filename}`;
        const optPath = `${idCompany}/${variantId}/opt/${filename}`;
        await supabase.storage.from('productos').remove([fullPath, optPath]);
      }

      await supabase
        .from('productos_imagenes')
        .delete()
        .eq('id', imageId)
        .eq('id_company', idCompany);

      // Ensure one principal remains
      let { data: imagesData } = await supabase
        .from('productos_imagenes')
        .select('id, url, principal, orden, variante_id')
        .eq('variante_id', variantId)
        .eq('id_company', idCompany)
        .order('orden', { ascending: true });

      if ((imagesData || []).length > 0 && !(imagesData || []).some((img: any) => img.principal)) {
        const firstId = (imagesData as any)[0].id;
        await supabase
          .from('productos_imagenes')
          .update({ principal: true })
          .eq('id', firstId)
          .eq('id_company', idCompany);

        // Re-fetch after setting principal
        const re = await supabase
          .from('productos_imagenes')
          .select('id, url, principal, orden, variante_id')
          .eq('variante_id', variantId)
          .eq('id_company', idCompany)
          .order('orden', { ascending: true });
        imagesData = re.data as any;
      }

      const updatedImages: ProductImage[] = (imagesData || []).map((img: any) => ({
        id: img.id,
        url: img.url,
        principal: img.principal,
        orden: img.orden,
        producto_variante_id: 0,
        variante_id: img.variante_id,
      }));

      setEditingVariant(prev => prev && prev.id === variantId ? { ...prev, images: updatedImages } : prev);
      setVariants(prev => prev.map(v => v.id === variantId ? { ...v, images: updatedImages } : v));
    } catch (err) {
      console.error('Error deleting image:', err);
      setError(err instanceof Error ? err.message : 'Error al eliminar la imagen');
    }
  };

  const handleReorderImages = async (_variantId: string, _imageId: number, _direction: 'up' | 'down') => {
    // Placeholder for future implementation
    return;
  };

  // Delete an entire product variant (and clean up images + product if empty)
  const handleDeleteVariant = async (variantId: string) => {
    try {
      setSavingProduct(true);
      setImageError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { data: profile } = await supabase
        .from('profiles')
        .select('id_company')
        .eq('id', user.id)
        .single();

      if (!(profile as any)?.id_company) throw new Error('No company associated with user');

      // 1) Get variant to know product_id
      const resolvedCompanyId = (profile as any).id_company as string;

      const { data: variantRow, error: variantSelErr } = await supabase
        .from('variantes')
        .select('id, product_id')
        .eq('id', variantId)
        .eq('id_company', resolvedCompanyId)
        .single();
      if (variantSelErr) throw variantSelErr;

      const productId = (variantRow as any)?.product_id as string | null;

      // 2) Load image filenames for this variant and delete from storage
      const { data: imagesData } = await supabase
        .from('productos_imagenes')
        .select('id, url')
        .eq('variante_id', variantId)
        .eq('id_company', resolvedCompanyId);

      // Resolve idCompany (UUID) once
      // resolvedCompanyId already computed above

      const filesToRemove: string[] = [];
      (imagesData || []).forEach((img: any) => {
        const filename = (img?.url as string) || '';
        if (filename && !filename.startsWith('http')) {
          filesToRemove.push(`${resolvedCompanyId}/${variantId}/full/${filename}`);
          filesToRemove.push(`${resolvedCompanyId}/${variantId}/opt/${filename}`);
        }
      });
      if (filesToRemove.length > 0) {
        try { await supabase.storage.from('productos').remove(filesToRemove); } catch {}
      }

      // 3) Delete image rows
      await supabase
        .from('productos_imagenes')
        .delete()
        .eq('variante_id', variantId)
        .eq('id_company', resolvedCompanyId);

      // 4) Delete variant row
      await supabase
        .from('variantes')
        .delete()
        .eq('id', variantId)
        .eq('id_company', resolvedCompanyId);

      // 5) If product has no more variants, delete product
      if (productId) {
        const { data: otherVariants } = await supabase
          .from('variantes')
          .select('id')
          .eq('product_id', productId)
          .eq('id_company', resolvedCompanyId)
          .limit(1);
        if (!otherVariants || otherVariants.length === 0) {
          try {
            await supabase
              .from('productos')
              .delete()
              .eq('id', productId)
              .eq('id_company', resolvedCompanyId);
          } catch {}
        }
      }

      // 6) Update local lists and selections
      setEditingVariant(null);
      setVariants((prev) => prev.filter((v) => v.id !== variantId));
      setSelectedVariants((prev) => {
        const next = prev.filter((id) => id !== variantId);
        mergeLastCrearAnuncioState({ selectedVariantIds: next });
        return next;
      });
    } catch (err) {
      console.error('Error deleting variant:', err);
      setError(err instanceof Error ? err.message : 'Error al eliminar el producto');
    } finally {
      setSavingProduct(false);
    }
  };

  const principalImageUrl = (variant: Variant): string | null => {
    const img = (variant.images || []).find(i => i.principal) || (variant.images || [])[0];
    if (!img?.url) return null;
    const urlKey = img.url;
    if (urlKey.startsWith('http')) return urlKey;
    const signed = signedUrls[urlKey];
    return signed?.thumbnail || null;
  };

  // Sign principal image URL for a variant on demand
  const ensurePrincipalImageSigned = async (variantId: string) => {
    const variant = variants.find(v => v.id === variantId);
    if (!variant) return;
    const img = (variant.images || []).find(i => i.principal) || (variant.images || [])[0];
    if (!img?.url) return;
    const key = img.url as string;
    
    if (key.startsWith('http')) {
      if (!signedUrls[key]) {
        setSignedUrls(prev => ({ ...prev, [key]: { full: key, thumbnail: key } }));
      }
      
      return;
    }
    if (!companyId) return;
    if (signedUrls[key] || loadingImageKeysRef.current.has(key)) return;

    loadingImageKeysRef.current.add(key);
    try {
      const pathVariantId = (img as any)?.variante_id ?? variant.id;
      const optFilePath = `${companyId}/${pathVariantId}/opt/${key}`;
      const fullFilePath = `${companyId}/${pathVariantId}/full/${key}`;
      

      let thumbSigned: string | null = null;
      let fullSigned: string | null = null;

      const { data: thumb } = await supabase.storage.from('productos').createSignedUrl(optFilePath, 3600);
      
      if (thumb?.signedUrl) {
        thumbSigned = thumb.signedUrl;
        const { data: full } = await supabase.storage.from('productos').createSignedUrl(fullFilePath, 3600);
        fullSigned = full?.signedUrl || thumbSigned;
        
      } else {
        const { data: full } = await supabase.storage.from('productos').createSignedUrl(fullFilePath, 3600);
        if (full?.signedUrl) {
          thumbSigned = full.signedUrl;
          fullSigned = full.signedUrl;
          
        }
      }

      let thumbOut = thumbSigned || null;
      let fullOut = fullSigned || null;
      if (thumbOut) {
        const thumbKeyStable = `productos/${optFilePath}`;
        const res = await getCachedImageByKey(thumbKeyStable, thumbOut);
        thumbOut = res.url;
        cachedKeysRef.current.add(thumbKeyStable);
      }
      if (fullOut) {
        const fullKeyStable = `productos/${fullFilePath}`;
        const res = await getCachedImageByKey(fullKeyStable, fullOut);
        fullOut = res.url;
        cachedKeysRef.current.add(fullKeyStable);
      }

      setSignedUrls(prev => ({ ...prev, [key]: { full: fullOut || key, thumbnail: thumbOut || fullOut || key } }));
      
    } catch (e) {
      console.error('[SPA] ensureSign:error', e);
      setSignedUrls(prev => ({ ...prev, [key]: { full: 'https://via.placeholder.com/300x300?text=Error', thumbnail: 'https://via.placeholder.com/300x300?text=Error' } }));
    } finally {
      loadingImageKeysRef.current.delete(key);
      
    }
  };

  // Filtered variants by query
  const filteredVariants = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return variants;
    return variants.filter(v => (v.nombre || '').toLowerCase().includes(q));
  }, [variants, query]);

  // Observe cards and load images on demand
  useEffect(() => {
    if (observerRef.current) observerRef.current.disconnect();

    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const el = entry.target as HTMLElement;
          const idAttr = el.getAttribute('data-variant-id');
          const vid = idAttr || '';
          if (vid) {
            
            ensurePrincipalImageSigned(vid as string);
          }
          observerRef.current?.unobserve(entry.target);
        }
      });
    }, { root: null, rootMargin: '200px 0px', threshold: 0.01 });

    // Start observing all visible cards
    filteredVariants.forEach((v) => {
      const el = cardRefs.current[String(v.id)];
      if (el) {
        
        observerRef.current!.observe(el);
      }
    });

    return () => observerRef.current?.disconnect();
  }, [filteredVariants, variants, companyId]);

  // Subscription/billing helpers (mirrors other steps)
  const getCurrentMonthRange = () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
    return { start: start.toISOString(), end: end.toISOString() };
  };
  const getBillingCycleRange = (paidPlanStartDate: string) => {
    const now = new Date();
    const subscriptionDate = new Date(paidPlanStartDate);
    const anchorDay = subscriptionDate.getDate();
    const daysInMonth = (year: number, month: number) => new Date(year, month + 1, 0).getDate();
    const anchorFor = (year: number, month: number) => {
      const dim = daysInMonth(year, month);
      const day = Math.min(anchorDay, dim);
      return new Date(year, month, day, 0, 0, 0, 0);
    };
    let start = anchorFor(now.getFullYear(), now.getMonth());
    if (start.getTime() > now.getTime()) {
      const prevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      start = anchorFor(prevMonth.getFullYear(), prevMonth.getMonth());
    }
    if (start.getTime() < subscriptionDate.getTime()) {
      start = new Date(
        subscriptionDate.getFullYear(), subscriptionDate.getMonth(), subscriptionDate.getDate(), 0, 0, 0, 0
      );
    }
    const nextMonth = new Date(start.getFullYear(), start.getMonth() + 1, 1);
    const end = anchorFor(nextMonth.getFullYear(), nextMonth.getMonth());
    return { start: start.toISOString(), end: end.toISOString() };
  };
  const checkSubscriptionLimits = async (): Promise<boolean> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');
      const { data: profile } = await supabase
        .from('profiles')
        .select('id_company')
        .eq('id', user.id)
        .single();
      if (!(profile as any)?.id_company) throw new Error('No company associated with user');
      const { data: companyProfile } = await supabase
        .from('company_profiles')
        .select('id_company, plan, imagenes_restantes, paid_plan_start_date')
        .eq('id_company', (profile as any).id_company)
        .single();
      if (!companyProfile) throw new Error('No company profile found');
      const resolvedCompanyId = (profile as any).id_company;
      if (!resolvedCompanyId) throw new Error('No company associated with user');
      const remaining = (companyProfile as any).imagenes_restantes as number | null | undefined;
      if (typeof remaining === 'number' && remaining <= 0) {
        const isFree = (companyProfile as any).plan === 'gratis';
        setSubscriptionLimitType(isFree ? 'free-trial-ended' : 'plan-limit-reached');
        setShowSubscriptionLimitModal(true);
        return false;
      }
      return true;
    } catch (err) {
      console.error('Error checking subscription limits (repeat):', err);
      return true;
    }
  };

  const getFormatName = (formato: string | undefined): string => {
    switch (formato) {
      case 'story': return '9:16';
      case 'square': return '1:1';
      case 'post':
      default: return '4:5';
    }
  };

  const fetchModelView = async (firstVariantId: string): Promise<string | null> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;
      const { data: profile } = await supabase
        .from('profiles')
        .select('id_company')
        .eq('id', user.id)
        .single();
      if (!(profile as any)?.id_company) return null;
      const idCompany = (profile as any).id_company as string;
      const { data, error } = await supabase
        .from('variantes')
        .select(`product_id, productos!inner ( id, model_view )`)
        .eq('id', firstVariantId)
        .eq('id_company', idCompany)
        .single();
      if (error) return null;
      const product = (data as any)?.productos;
      return (product?.model_view as string | null) ?? null;
    } catch {
      return null;
    }
  };

  const resolvePoseForModelView = (modelView: string | null, savedPose?: string): string | undefined => {
    // If savedPose exists, keep it
    if (savedPose) return savedPose;
    if (modelView === 'calzado') return 'foco-zapatos';
    if (['torso', 'manos', 'cabeza', 'cuerpo_completo', 'na', null].includes(modelView as any)) return 'plano-superior';
    return undefined;
  };

  const buildRepeatPayload = async (): Promise<Record<string, any> | null> => {
    const last = loadLastCrearAnuncioState();
    if (!last) return null;
    const variantIds = selectedVariants.map((s) => String(s)).map((s) => s.trim()).filter(Boolean);
    if (variantIds.length === 0) return null;
    const isFashion = ((last.rubro as string | undefined) ?? FASHION_LABEL) === FASHION_LABEL;
    const compose = (!isFashion && (last.modo || 'rapido') === 'personalizado')
      ? 'producto'
      : ((last.composicion === 'modelo' || last.composicion === 'producto') ? last.composicion : 'producto');
    const tipo = last.tipo === 'imagen' ? 'imagen' : 'imagen';
    const formato = (last.formato === 'post' || last.formato === 'story' || last.formato === 'square') ? last.formato : 'post';
    const cantidadImagenes = typeof last.cantidadImagenes === 'number' && last.cantidadImagenes > 0 ? Math.min(8, Math.max(1, last.cantidadImagenes)) : (last.modo === 'personalizado' ? 2 : 4);
    const formatName = getFormatName(formato);
    const mode = variantIds.length > 1 ? 'multiple' : 'single';

    // Pose compatibility when composing with model
    let pose: string | undefined = last.pose || undefined;
    if (compose === 'modelo') {
      const modelView = await fetchModelView(String(variantIds[0]));
      pose = resolvePoseForModelView(modelView, last.pose || undefined);
    }

    const payload: Record<string, any> = {
      variante_id: variantIds,
      mode,
      modo: last.modo || 'rapido',
      estilo: 'automatico',
      tipo,
      cantidad_imagenes: tipo === 'imagen' ? cantidadImagenes : undefined,
      Format: formatName,
      objetivo: last.objetivo || undefined,
      detalles_oferta: last.detallesOferta?.trim() ? last.detallesOferta.trim() : undefined,
      composicion: compose,
    };
    if (pose) payload.pose = pose;

    // Background
    const bg = last.background;
    const bgMode = bg?.mode || 'blanco';
    payload.background_mode = bgMode;
    if (bgMode === 'elegido') {
      if (typeof bg?.id === 'number' && Number.isFinite(bg.id)) payload.background_id = bg.id;
      if (bg?.url && !(typeof bg?.id === 'number' && Number.isFinite(bg.id))) payload.background_url = bg.url;
    } else if (bgMode === 'blanco' && (last.modo || 'rapido') === 'personalizado') {
      payload.background_id = 5;
    }
    if (bgMode === 'descripcion' && bg?.text?.trim()) payload.background_text = bg.text.trim();

    // Composition text/template (when available from last state and composing with model)
    const comp = last.composition;
    if (compose === 'modelo' && comp) {
      payload.composition_mode = comp.enabled ? true : false;
      if (comp.enabled) {
        if (typeof comp.selectedCompId === 'number' && Number.isFinite(comp.selectedCompId)) payload.composition_id = comp.selectedCompId;
        if (!payload.composition_id && comp.selectedCompUrl) payload.composition_url = comp.selectedCompUrl;
        if (comp.compositionText && comp.compositionText.trim()) payload.composition_text = comp.compositionText.trim();
      }
    }
    // Modo Pro
    if (last.modoPro) payload.modo_pro = true;
    return payload;
  };

  // Preload images by fetching signed URLs and loading them into browser cache
  type PreloadedImage = { id: number; fileName: string; signedUrl: string; favorito: boolean };
  const preloadGeneratedImages = async (imageIds: number[], companyId: string): Promise<PreloadedImage[]> => {
    const preloaded: PreloadedImage[] = [];
    const maxAttempts = 10;
    const delay = (ms: number) => new Promise(res => setTimeout(res, ms));

    for (const imageId of imageIds) {
      let loaded = false;
      for (let attempt = 1; attempt <= maxAttempts && !loaded; attempt++) {
        try {
          const { data: imageData } = await supabase
            .from('imagenes_generadas')
            .select('file_name, favorito')
            .eq('id', imageId)
            .eq('id_company', companyId)
            .single();

          if (!imageData?.file_name) {
            await delay(1000);
            continue;
          }

          const filePath = `${companyId}/full/${imageData.file_name}`;
          const { data: signedUrlData } = await supabase.storage.from('generadas').createSignedUrl(filePath, 3600);
          
          if (!signedUrlData?.signedUrl) {
            await delay(1000);
            continue;
          }

          // Preload image into browser cache
          await new Promise<void>((resolve) => {
            const img = new Image();
            img.onload = () => resolve();
            img.onerror = () => resolve();
            img.src = signedUrlData.signedUrl;
          });

          preloaded.push({
            id: imageId,
            fileName: imageData.file_name,
            signedUrl: signedUrlData.signedUrl,
            favorito: imageData.favorito === true,
          });
          loaded = true;
        } catch (err) {
          console.warn(`Preload attempt ${attempt} failed for image ${imageId}:`, err);
          await delay(1000);
        }
      }
    }
    return preloaded;
  };

  const performRepeatGeneration = async (more: boolean = false) => {
    try {
      setRepeatError(null);
      const can = await checkSubscriptionLimits();
      if (!can) return;
      // Ensure overlay mounts with completeNow=false to avoid instant completion from previous run
      setCompleteRepeatNow(false);
      // Use a macrotask to ensure a separate render commit before mounting overlay
      await new Promise((r) => setTimeout(r, 0));
      setGeneratingRepeat(true);
      try { if (!more) trackInteraction('repetir_generacion'); } catch {}

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No se encontró el usuario');
      const payload = await buildRepeatPayload();
      if (!payload) throw new Error('No hay una configuración previa para repetir.');
      if (more && payload.tipo === 'imagen') payload.cantidad_imagenes = 2;

      // Ensure user id included
      payload.user_id = user.id;

      // Persist resolved pose in memory so subsequent 'Generar más' includes it
      try {
        if (payload.pose) {
          mergeLastCrearAnuncioState({ pose: payload.pose as string });
        }
      } catch {}

      try { console.log('[SeleccionarProductosAnuncio] performRepeatGeneration payload', payload); } catch {}
      const response = await fetch(GENERATE_AD_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': GENERATE_AUTH_TOKEN }, body: JSON.stringify(payload)
      });
      let __respTextRP: string | null = null;
      try { __respTextRP = await response.clone().text(); } catch {}
      try { console.log('[SeleccionarProductosAnuncio] performRepeatGeneration response', { status: response.status, ok: response.ok, text: __respTextRP }); } catch {}
      if (!response.ok) {
        throw new Error(__respTextRP || `Error ${response.status}`);
      }
      let data: any = null;
      try { data = await response.json(); } catch (e) { console.error('[SeleccionarProductosAnuncio] performRepeatGeneration json parse error', e); }
      try { console.log('[SeleccionarProductosAnuncio] performRepeatGeneration json', data); } catch {}
      if (data?.response === 'success' && Array.isArray(data?.imagenes_generadas_ids) && data.imagenes_generadas_ids.length > 0) {
        const genIds = data.imagenes_generadas_ids as number[];
        const compId = (data.id_company as string) || companyId || '';
        
        // Preload images before navigating
        let preloadedImages: PreloadedImage[] = [];
        try {
          preloadedImages = await preloadGeneratedImages(genIds, compId);
        } catch (preloadErr) {
          console.warn('[SeleccionarProductosAnuncio] preload failed:', preloadErr);
        }
        
        try { window.sessionStorage.setItem('atlas:crear-anuncio:has-generated', '1'); setHasGeneratedThisSession(true); } catch {}
        setShowRepeatConfirm(false);
        // Navigate directly to generated images page with preloaded images
        try { console.log('[SeleccionarProductosAnuncio] navigating to imagenes-generadas', { genIds, compId }); } catch {}
        navigatedAfterRepeatRef.current = true;
        navigate('/redes-sociales/imagenes-generadas', { 
          state: { imageIds: genIds, companyId: compId, preloadedImages } 
        });
        // Also complete the overlay for cleanliness in case route caching delays unmount
        setCompleteRepeatNow(true);
      } else {
        throw new Error('No se recibieron imágenes generadas.');
      }
    } catch (e) {
      console.error(e);
      setRepeatError('Se produjo un error. No se consumieron imágenes. Por favor, volvé a intentarlo.');
      setCompleteRepeatNow(true);
    } finally {
      
    }
  };

  

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.has('modo')) {
      mergeLastCrearAnuncioState({ modo });
    }
  }, [modo, location.search]);

  

  const greeting = getGreeting();
  const greetingText = shortName ? `${greeting}, ${shortName}.` : `${greeting}.`;

  return (
    <>
    <div className="fixed inset-0 bg-white sm:bg-transparent pointer-events-none sm:hidden" aria-hidden="true" />
    <div className="relative p-4 pb-28 sm:p-8 sm:pb-0 max-w-7xl mx-auto bg-white sm:bg-transparent min-h-screen">
      {/* Mobile header with greeting */}
      <div className="sm:hidden mt-12 mb-4">
        <h1 className="text-2xl font-semibold text-gray-900 text-center mt-20 mb-10">{greetingText}</h1>

        {showUpsell && (
          <div className="mb-4 bg-blue-50 rounded-2xl p-4 relative">
            <p className="text-sm text-gray-800 pb-12">
            ¿Te está gustando Atlas? Suscribite a un plan y conocé todas sus funciones.
            </p>
            <button
              type="button"
              onClick={() => navigate('/subscription')}
              className="absolute bottom-4 right-4 inline-flex items-center gap-2 h-10 px-4 bg-blue-500 hover:bg-blue-600 text-white rounded-full shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Ver planes
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}

        {showBrandAnalysisBanner && (
          <div className="mb-4 bg-blue-50 rounded-2xl p-4 relative">
            <div className="pb-12">
              <div className="flex items-center gap-2 mb-1">
                <Sparkles className="w-4 h-4 text-blue-500" />
                <h3 className="text-sm font-semibold text-gray-900">Mejorá tus fotos</h3>
              </div>
              <p className="text-sm text-gray-600">
                Contale a la IA sobre tu marca para que genere fotos adaptadas a su estilo
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate('/redes-sociales/descripcion-brand')}
              className="absolute bottom-4 right-4 inline-flex items-center gap-2 h-10 px-4 bg-blue-500 hover:bg-blue-600 text-white rounded-full shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Mejorar fotos
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}

        <h2 className="text-base font-medium text-gray-900 mt-6">Seleccioná productos</h2>
        <p className="text-sm text-gray-600 mt-1">Los anuncios generados se crearán utilizando los productos seleccionados.</p>
      </div>

      {/* Desktop header with greeting */}
      <div className="hidden sm:block mb-6">
        <h1 className="text-3xl font-semibold text-gray-900 text-center">{greetingText}</h1>
        {randomMessage && (
          <p className="text-base text-gray-600 text-center mt-2 mb-16">{randomMessage}</p>
        )}

        {showUpsell && (
          <div className="mb-4 bg-blue-50 rounded-2xl p-4 relative sm:flex sm:items-center sm:justify-between sm:gap-3">
            <p className="text-sm text-gray-800 pb-12 sm:pb-0">
            ¿Te está gustando Atlas? Suscribite a un plan y conocé todas sus funciones.
            </p>
            <button
              type="button"
              onClick={() => navigate('/subscription')}
              className="absolute bottom-4 right-4 sm:relative sm:bottom-auto sm:right-auto inline-flex items-center gap-2 h-10 px-4 bg-blue-500 hover:bg-blue-600 text-white rounded-full shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Ver planes
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}

        {showBrandAnalysisBanner && (
          <div className="mb-4 bg-blue-50 rounded-2xl p-4 relative sm:flex sm:items-center sm:justify-between sm:gap-3">
            <div className="pb-12 sm:pb-0">
              <div className="flex items-center gap-2 mb-1">
                <Sparkles className="w-4 h-4 text-blue-500" />
                <h3 className="text-sm font-semibold text-gray-900">Mejorá tus fotos</h3>
              </div>
              <p className="text-sm text-gray-600">
                Contale a la IA sobre tu marca para que genere fotos adaptadas a su estilo
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate('/redes-sociales/descripcion-brand')}
              className="absolute bottom-4 right-4 sm:relative sm:bottom-auto sm:right-auto inline-flex items-center gap-2 h-10 px-4 bg-blue-500 hover:bg-blue-600 text-white rounded-full shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Mejorar fotos
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}

        <div className="mt-4">
          <h2 className="text-lg font-semibold text-gray-900">Seleccioná productos</h2>
          <p className="text-gray-600 text-sm sm:text-base mt-1">Elegí hasta 3 productos. Los anuncios generados se crearán utilizando los productos seleccionados.</p>
        </div>
      </div>

      {/* Search bar and Add product button */}
      <div className="mb-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="relative flex-1">
            <label htmlFor="search-productos" className="sr-only">Buscar productos</label>
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
            <input
              id="search-productos"
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar productos..."
              className="w-full pl-10 pr-3 h-11 sm:h-12 rounded-2xl bg-gray-50 sm:bg-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 text-sm placeholder:text-gray-400"
            />
          </div>
          <button
            type="button"
            onClick={() => setShowAddForm(true)}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 h-11 sm:h-12 px-5 sm:px-8 bg-blue-500 hover:bg-blue-600 text-white rounded-2xl shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 text-sm font-medium"
          >
            <Plus className="w-4 h-4" />
            Agregar producto
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 flex items-center text-red-700 bg-red-50 border border-red-200 rounded-md p-3">
          <AlertCircle className="w-4 h-4 mr-2" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="bg-white border border-gray-200 rounded-md overflow-hidden">
              <div className="w-full aspect-square bg-gray-100 animate-pulse" />
              <div className="p-3">
                <div className="h-4 bg-gray-100 rounded w-2/3 animate-pulse" />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <>
          {variants.length === 0 ? (
            <div className="bg-white border-0 sm:border sm:border-gray-200 rounded-md p-6 sm:p-8 text-center">
              <p className="text-gray-600">No tenés productos cargados todavía.</p>
              <button
                type="button"
                onClick={() => setShowAddForm(true)}
                className="mt-4 inline-flex items-center justify-center h-12 px-6 bg-blue-500 hover:bg-blue-600 text-white rounded-full shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <Plus className="w-4 h-4 mr-2" /> Agregar producto
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {filteredVariants.map(variant => {
                const imgUrl = principalImageUrl(variant);
                const selected = selectedVariants.includes(variant.id);
                return (
                  <button
                    key={variant.id}
                    type="button"
                    onClick={() => handleVariantSelect(variant.id)}
                    className={`relative text-left bg-white border-0 ${selected ? 'ring-2 ring-blue-500' : ''} rounded-2xl overflow-hidden focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 group`}
                    ref={(el) => { cardRefs.current[variant.id] = el; }}
                    data-variant-id={variant.id}
                  >
                    <div className="relative pt-0 px-0 sm:pt-3 sm:px-3">
                      {imgUrl ? (
                        <img src={imgUrl} alt={variant.nombre} loading="lazy" className="w-full aspect-square object-cover rounded-b-2xl sm:rounded-lg" />
                      ) : (
                        <div className="w-full aspect-square bg-gray-100 flex items-center justify-center rounded-b-2xl sm:rounded-lg">
                          <ImageIcon className="w-8 h-8 text-gray-400" />
                        </div>
                      )}
                      <div className={`absolute top-2 right-2 sm:top-4 sm:right-4 w-7 h-7 rounded-full ${selected ? 'bg-blue-500 text-white' : 'bg-white/80 text-gray-300 group-hover:text-gray-400 backdrop-blur-sm'} flex items-center justify-center shadow-none transition-colors`}>
                        <Check className="w-4 h-4" />
                      </div>
                    </div>
                    <div className="p-3">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm text-gray-900 truncate font-medium py-1">{variant.nombre}</p>
                        {selected && (
                          <div className="flex items-center gap-1.5 shrink-0">
                            <span
                              role="button"
                              tabIndex={0}
                              title="Editar"
                              aria-label="Editar"
                              className="p-1.5 rounded-md text-gray-600 hover:text-gray-800 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                              onClick={(e) => handleEditIconClick(e, variant.id)}
                              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleEditIconClick(e as any, variant.id); } }}
                            >
                              <Pencil className="w-4 h-4" />
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </button>
                )
              })}
            </div>
          )}
        </>
      )}

    {/* Spacer for fixed desktop bar (to avoid overlap) */}
    {selectedVariants.length > 0 && <div className="hidden sm:block h-16" />}

    {/* Fixed bottom bar (desktop only) */}
    {selectedVariants.length > 0 && (
      <div className="hidden sm:block fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-3 z-30">
        <div className="max-w-7xl mx-auto relative flex items-center justify-between gap-4">
          <div className="absolute left-1/2 -translate-x-1/2 text-sm text-gray-600 pointer-events-none">
            {selectedVariants.length} {selectedVariants.length === 1 ? 'producto seleccionado' : 'productos seleccionados'}
          </div>
          <div className="flex items-center gap-3 ml-auto">
            <button
              type="button"
              className="inline-flex items-center gap-2 px-4 h-10 bg-blue-500 text-white rounded-full shadow-sm hover:bg-blue-600 focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 text-sm"
              onClick={handleVideoGenerationClick}
            >
              <Video className="w-4 h-4" />
              Generar video
            </button>
            <button
              type="button"
              className="inline-flex items-center gap-2 px-4 h-10 bg-blue-500 text-white rounded-full shadow-sm hover:bg-blue-600 focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 text-sm whitespace-nowrap"
              onClick={() => {
                if (isBetaTester) {
                  navigate(`/redes-sociales/crear-anuncio/prueba-nueva-interfaz?ids=${selectedVariants.join(',')}`);
                } else {
                  navigate(`/redes-sociales/crear-anuncio/seleccionar-modo?ids=${selectedVariants.join(',')}`);
                }
              }}
            >
              <ImageIcon className="w-4 h-4" />
              Generar imagen
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Mobile spacer for fixed bottom bar */}
    {selectedVariants.length > 0 && (
      <div className="sm:hidden" style={{ height: 'calc(env(safe-area-inset-bottom, 0px) + 68px)' }} />
    )}

    {selectedVariants.length === 0 && isFreePlan && (
    <a
      href={`https://wa.me/5491122635392?text=${encodeURIComponent(`Buenas, soy ${shortName || ''} de ${companyName || ''}. Me contactaba porque tenia una duda.`)}`}
      target="_blank"
      rel="noopener noreferrer"
      className="sm:hidden fixed left-3 z-30 inline-flex items-center gap-2 px-4 h-10 rounded-full border border-gray-200 bg-white text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 text-sm"
      style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)' }}
      aria-label="Contactanos por WhatsApp"
      title="Contactanos por WhatsApp"
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor" className="text-gray-700">
        <path fillRule="evenodd" clipRule="evenodd" d="M19.0781 4.9151C17.1693 2.99084 14.6008 1.93602 11.868 1.93945C6.25977 1.93945 1.70409 6.49408 1.7002 12.1084C1.69925 13.8729 2.15811 15.6037 2.99707 17.1318L1.60547 22.4004L7.00879 21.0381C8.48438 21.8008 10.1602 22.2 11.8633 22.2002H11.8691C17.4756 22.2002 22.0322 17.6445 22.0361 12.0293C22.0381 9.30713 20.9863 6.83936 19.0781 4.9151ZM11.8682 20.5254H11.8633C10.3457 20.5254 8.85645 20.1484 7.55371 19.4424L7.24609 19.2686L4.12305 20.1025L4.97266 17.0557L4.7793 16.7334C3.99023 15.375 3.57031 13.7646 3.57129 12.1094C3.57422 7.52832 7.29004 3.81543 11.873 3.81543C14.1475 3.81055 16.2803 4.6875 17.877 6.29639C19.4736 7.90527 20.3447 10.0459 20.3438 12.3281C20.3398 16.9102 16.624 20.5254 11.8682 20.5254ZM16.5898 14.2168C16.3389 14.0908 15.1201 13.4922 14.8896 13.4077C14.6592 13.3232 14.4922 13.2812 14.3242 13.5333C14.1562 13.7854 13.6816 14.3413 13.5322 14.5088C13.3828 14.6762 13.2344 14.6967 12.9834 14.5708C12.7324 14.4448 11.9302 14.1885 10.9932 13.3525C10.2666 12.7041 9.77832 11.9102 9.62891 11.6582C9.47949 11.4062 9.61426 11.2671 9.7432 11.1392C9.85742 11.0244 9.99609 10.8384 10.125 10.689C10.2539 10.5396 10.2969 10.4346 10.3809 10.2666C10.4648 10.0986 10.4219 9.94921 10.3594 9.82323C10.2969 9.69726 9.81152 8.47364 9.60254 7.96973C9.39844 7.47949 9.19141 7.54196 9.03418 7.53417C8.88477 7.52734 8.71777 7.52539 8.54980 7.52539C8.38184 7.52539 8.10742 7.58789 7.87695 7.83986C7.64648 8.09183 7.00586 8.69043 7.00586 9.91308C7.00586 11.1357 7.89844 12.3164 8.02734 12.4844C8.15625 12.6523 9.77637 15.1104 12.2227 16.1816C12.8281 16.4414 13.2998 16.5977 13.667 16.7158C14.2852 16.9102 14.8477 16.8828 15.292 16.8193C15.791 16.749 16.7783 16.2207 16.9873 15.6318C17.1963 15.043 17.1963 14.5391 17.1338 14.5088C17.0713 14.4785 16.8408 14.3428 16.5898 14.2168Z"/>
    </svg>
    Contactanos
  </a>
)}

    {/* Mobile bottom bar with Continue + Repeat */}
    {selectedVariants.length > 0 && (
      <div className="sm:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-3 z-30">
        <div className="flex items-center gap-3">
          <button
            type="button"
            className="flex-1 inline-flex items-center justify-center gap-2 px-4 h-10 bg-blue-500 hover:bg-blue-600 text-white rounded-full shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 text-sm"
            onClick={handleVideoGenerationClick}
          >
            <Video className="w-4 h-4" />
            Generar video
          </button>
          <button
            type="button"
            className="flex-1 inline-flex items-center justify-center gap-2 px-4 h-10 bg-blue-500 hover:bg-blue-600 text-white rounded-full shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 text-sm"
            onClick={() => {
              if (isBetaTester) {
                navigate(`/redes-sociales/crear-anuncio/prueba-nueva-interfaz?ids=${selectedVariants.join(',')}`);
              } else {
                navigate(`/redes-sociales/crear-anuncio/seleccionar-modo?ids=${selectedVariants.join(',')}`);
              }
            }}
          >
            <ImageIcon className="w-4 h-4" />
            Generar imagen
          </button>
        </div>
      </div>
    )}

    {/* Repeat confirmation popup */}
    {showRepeatConfirm && (
      <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4">
        <div className="bg-white w-full max-w-md rounded-md shadow-lg p-5 relative">
          <button
            type="button"
            onClick={() => setShowRepeatConfirm(false)}
            className="absolute top-3 right-3 p-2 text-gray-500 hover:text-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
          <h3 className="text-lg font-semibold text-gray-900">Repetir generación</h3>
          {repeatError && (
            <div className="mt-3 bg-red-50 text-red-700 px-4 py-3 text-sm">{repeatError}</div>
          )}
          <p className="text-sm text-gray-600 mt-2">
            {(() => {
              const last = loadLastCrearAnuncioState();
              const count = (last?.tipo === 'imagen')
                ? (typeof last?.cantidadImagenes === 'number' && last.cantidadImagenes > 0
                  ? Math.min(8, Math.max(1, last.cantidadImagenes))
                  : ((last?.modo || 'rapido') === 'personalizado' ? 2 : 4))
                : 0;
              return `Se generarán ${count} imágenes con las mismas opciones que seleccionaste antes.`;
            })()}
          </p>
          <div className="mt-4 flex items-center justify-end gap-2">
            <button
              type="button"
              className="px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-md"
              onClick={() => setShowRepeatConfirm(false)}
            >
              Cancelar
            </button>
            <button
              type="button"
              disabled={generatingRepeat}
              className={`inline-flex items-center gap-2 px-4 h-10 rounded-full shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 text-sm ${generatingRepeat ? 'bg-gray-300 text-white cursor-not-allowed' : 'bg-blue-500 hover:bg-blue-600 text-white'}`}
              onClick={() => performRepeatGeneration(false)}
            >
              {generatingRepeat ? (<><Loader2 className="w-4 h-4 animate-spin" /> Generando</>) : (<><Sparkles className="w-4 h-4" /> Generar</>)}
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Loading overlay for repeat generation */}
    {generatingRepeat && (
      <LoadingAnimation
        message="Generando imágenes..."
        durationMs={progressDurationMs}
        completeNow={completeRepeatNow}
        onCompleted={() => {
          try { console.log('[SeleccionarProductosAnuncio] LoadingAnimation onCompleted fired'); } catch {}
          // Navigation happens in performRepeatGeneration, just clean up here
          setGeneratingRepeat(false);
        }}
      />
    )}

    {/* Results overlay for repeat generation */}
    {repeatResultsOpen && (
      <GeneratedImagesInline
        imageIds={repeatGeneratedIds}
        companyId={repeatCompanyId}
        onClose={() => {
          setRepeatResultsOpen(false);
          // Clear selected products when closing results
          setSelectedVariants([]);
          mergeLastCrearAnuncioState({ selectedVariantIds: [] });
        }}
        onGenerateMore={() => performRepeatGeneration(true)}
        onReady={() => { /* no-op */ }}
      />
    )}

    <SubscriptionLimitModal
      isOpen={showSubscriptionLimitModal}
      onClose={() => setShowSubscriptionLimitModal(false)}
      type={subscriptionLimitType}
    />

    {/* Video plan restriction modal */}
    {showVideoPlanRestriction && (
      <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4">
        <div className="bg-white w-full max-w-sm rounded-2xl shadow-lg p-6 relative text-center">
          <button
            type="button"
            onClick={() => setShowVideoPlanRestriction(false)}
            className="absolute top-3 right-3 p-2 text-gray-500 hover:text-gray-700 rounded-md focus:outline-none"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-purple-100 flex items-center justify-center">
            <Video className="w-8 h-8 text-purple-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Función exclusiva</h3>
          <p className="text-sm text-gray-600 mb-5">
            La generación de videos está disponible únicamente para los usuarios de los planes <span className="font-semibold">Pro</span> y <span className="font-semibold">Ultra</span>.
          </p>
          {userPlan === 'gratis' ? (
            <button
              type="button"
              onClick={() => {
                setShowVideoPlanRestriction(false);
                navigate('/subscription');
              }}
              className="inline-flex items-center justify-center w-full h-10 bg-blue-500 hover:bg-blue-600 text-white rounded-full text-sm font-medium"
            >
              Ver planes
            </button>
          ) : (
            // Plan básico (emprendimiento)
            isArgentina ? (
              <button
                type="button"
                onClick={() => {
                  setShowVideoPlanRestriction(false);
                  navigate('/subscription');
                }}
                className="inline-flex items-center justify-center w-full h-10 bg-blue-500 hover:bg-blue-600 text-white rounded-full text-sm font-medium"
              >
                Mejorar plan
              </button>
            ) : (
              <button
                type="button"
                onClick={handleOpenBillingPortal}
                disabled={openingBillingPortal}
                className="inline-flex items-center justify-center w-full h-10 bg-blue-500 hover:bg-blue-600 disabled:opacity-70 text-white rounded-full text-sm font-medium"
              >
                {openingBillingPortal ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Mejorar plan'}
              </button>
            )
          )}
        </div>
      </div>
    )}

    {/* Video multiple products warning modal */}
    {showVideoMultipleProductsWarning && (
      <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4">
        <div className="bg-white w-full max-w-sm rounded-2xl shadow-lg p-6 relative text-center">
          <button
            type="button"
            onClick={() => setShowVideoMultipleProductsWarning(false)}
            className="absolute top-3 right-3 p-2 text-gray-500 hover:text-gray-700 rounded-md focus:outline-none"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-purple-100 flex items-center justify-center">
            <Video className="w-8 h-8 text-purple-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Un producto a la vez</h3>
          <p className="text-sm text-gray-600 mb-5">
            Por el momento, la generación de video solo admite generar de a 1 producto a la vez.
          </p>
          <button
            type="button"
            onClick={() => setShowVideoMultipleProductsWarning(false)}
            className="inline-flex items-center justify-center w-full h-10 bg-blue-500 hover:bg-blue-600 text-white rounded-full text-sm font-medium"
          >
            Entendido
          </button>
        </div>
      </div>
    )}

      {showAddForm && (
        <VariantForm
          editingVariant={null}
          newProduct={newProduct}
          loading={savingProduct}
          uploadingImages={{}}
          imageError={imageError}
          onCancel={() => setShowAddForm(false)}
          onSave={handleSaveProduct}
          onImageUpload={() => {}}
          onDeleteImage={() => {}}
          onSetPrincipalImage={() => {}}
          onReorderImages={() => {}}
          setEditingVariant={() => {}}
          setNewProduct={setNewProduct}
          rubro={userRubro}
        />
      )}

      {editingVariant && (
        <VariantForm
          editingVariant={editingVariant}
          newProduct={newProduct}
          loading={savingProduct}
          uploadingImages={uploadingImages}
          imageError={imageError}
          onCancel={() => setEditingVariant(null)}
          onSave={handleSaveProduct}
          onImageUpload={handleImageUpload}
          onDeleteImage={handleDeleteImage}
          onSetPrincipalImage={handleSetPrincipalImage}
          onReorderImages={handleReorderImages}
          setEditingVariant={setEditingVariant}
          setNewProduct={setNewProduct}
          onDeleteVariant={handleDeleteVariant}
          rubro={userRubro}
        />
      )}
    </div>
    </>
  );
};

export default SeleccionarProductosAnuncio;
